import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import { LucideIcon } from "lucide-react";
import {
  Code,
  Users,
  Settings,
  TrendingUp,
  Lightbulb,
  Rocket,
  Database,
  Cloud,
  Smartphone,
  Shield,
  Network,
  MessageCircle,
  GraduationCap,
  Handshake,
} from "lucide-react";
import "./HexagonSkills.css";

interface SkillData {
  value: number;
  label: string;
  color: string;
  icon?: LucideIcon;
  description?: string;
}

interface HexagonSkillsProps {
  skills?: Record<string, SkillData> | null;
  title?: string;
  editable?: boolean;
  onSkillsChange?: (skills: Record<string, SkillData>) => void;
  size?: "small" | "medium" | "large";
}

// Utility function to get icon for skill key
const getIconForSkill = (skillKey: string): LucideIcon => {
  const iconMap: Record<string, LucideIcon> = {
    technical: Code,
    softSkills: Users,
    devops: Settings,
    analytics: TrendingUp,
    innovation: Lightbulb,
    leadership: Rocket,
    database: Database,
    cloud: Cloud,
    mobile: Smartphone,
    security: Shield,
    projectManagement: Network,
    communication: MessageCircle,
    learning: GraduationCap,
    collaboration: Handshake,
  };
  return iconMap[skillKey] || Code;
};

// Helper function to get size-based values
const getSizeValue = <T,>(size: string, small: T, large: T, medium: T): T => {
  if (size === "small") return small;
  if (size === "large") return large;
  return medium;
};

const HexagonSkills: React.FC<HexagonSkillsProps> = ({
  skills = null,
  title,
  editable = false,
  onSkillsChange = null,
  size = "medium",
}) => {
  const { t } = useTranslation(["common"]);
  const displayTitle = title ?? t("common:skills.title");
  // Default skills data structure
  const defaultSkills: Record<string, SkillData> = {
    technical: {
      value: 70,
      label: t("common:skills.labels.technical", { defaultValue: "Technical" }),
      icon: Code,
      color: "#1976D2",
    },
    softSkills: {
      value: 80,
      label: t("common:skills.labels.softSkills", {
        defaultValue: "Soft Skills",
      }),
      icon: Users,
      color: "#4CAF50",
    },
    devops: {
      value: 60,
      label: t("common:skills.labels.devops", { defaultValue: "DevOps" }),
      icon: Settings,
      color: "#FF9800",
    },
    analytics: {
      value: 75,
      label: t("common:skills.labels.analytics", { defaultValue: "Analytics" }),
      icon: TrendingUp,
      color: "#9C27B0",
    },
    innovation: {
      value: 85,
      label: t("common:skills.labels.innovation", {
        defaultValue: "Innovation",
      }),
      icon: Lightbulb,
      color: "#F44336",
    },
    leadership: {
      value: 65,
      label: t("common:skills.labels.leadership", {
        defaultValue: "Leadership",
      }),
      icon: Rocket,
      color: "#00BCD4",
    },
  };

  const [skillsData, setSkillsData] = useState<Record<string, SkillData>>(
    () => {
      if (skills) {
        const processedSkills: Record<string, SkillData> = {};
        Object.keys(skills).forEach((key) => {
          const skill = skills[key];
          processedSkills[key] = {
            value: skill.value || 0,
            label: skill.label || key,
            color: skill.color || "#1976D2",
            icon: skill.icon || getIconForSkill(key),
            description: skill.description || "",
          };
        });
        return processedSkills;
      }
      return defaultSkills;
    },
  );
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [hoveredSkill, setHoveredSkill] = useState<string | null>(null);
  const skillsDataRef = useRef<Record<string, SkillData> | null>(null);

  // Initialize ref with initial state on mount only
  useEffect(() => {
    skillsDataRef.current = skillsData;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (skills) {
      // Ensure skills have proper structure with icons
      const processedSkills: Record<string, SkillData> = {};
      Object.keys(skills).forEach((key) => {
        const skill = skills[key];
        processedSkills[key] = {
          value: skill.value || 0,
          label: skill.label || key,
          color: skill.color || "#1976D2",
          icon: skill.icon || getIconForSkill(key),
          description: skill.description || "",
        };
      });
      // Always update to ensure component reflects prop changes
      setSkillsData(processedSkills);
      skillsDataRef.current = processedSkills;
    }
  }, [skills]);

  // Update ref when skillsData changes
  useEffect(() => {
    skillsDataRef.current = skillsData;
  }, [skillsData]);

  // Update local state only (for visual feedback during drag)
  const handleSkillChangeLocal = (skillKey: string, newValue: number) => {
    const updatedSkills = {
      ...skillsData,
      [skillKey]: {
        ...skillsData[skillKey],
        value: Math.max(0, Math.min(100, newValue)),
      },
    };
    setSkillsData(updatedSkills);
    skillsDataRef.current = updatedSkills;
  };

  // Handle slider drag end - get the latest value from the event
  const handleSliderMouseUp = (
    e: React.MouseEvent<HTMLInputElement> | React.TouchEvent<HTMLInputElement>,
    skillKey: string,
  ) => {
    // Get the final value from the event target to ensure we have the latest
    const target = e.target as HTMLInputElement;
    const finalValue = Number.parseInt(target.value, 10);
    // Update local state and ref one more time to ensure it's in sync
    const updatedSkills = {
      ...skillsData,
      [skillKey]: {
        ...skillsData[skillKey],
        value: Math.max(0, Math.min(100, finalValue)),
      },
    };
    setSkillsData(updatedSkills);
    skillsDataRef.current = updatedSkills;
    // Commit immediately with the updated skills
    if (onSkillsChange) {
      onSkillsChange(updatedSkills);
    }
  };

  // Handle number input change (commit on blur or Enter)
  const handleNumberInputChange = (skillKey: string, newValue: number) => {
    handleSkillChangeLocal(skillKey, newValue);
  };

  const handleNumberInputBlur = (
    e: React.FocusEvent<HTMLInputElement>,
    skillKey: string,
  ) => {
    // Get the final value from the input to ensure we have the latest
    const finalValue = Number.parseInt(e.target.value, 10) || 0;
    // Update local state and ref one more time to ensure it's in sync
    const updatedSkills = {
      ...skillsData,
      [skillKey]: {
        ...skillsData[skillKey],
        value: Math.max(0, Math.min(100, finalValue)),
      },
    };
    setSkillsData(updatedSkills);
    skillsDataRef.current = updatedSkills;
    // Commit immediately with the updated skills
    if (onSkillsChange) {
      onSkillsChange(updatedSkills);
    }
  };

  const handleNumberInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ) => {
    if (e.key === "Enter") {
      (e.target as HTMLInputElement).blur(); // This will trigger onBlur which commits the change
    }
  };

  const getSkillPosition = (skillKey: string, index: number) => {
    const skillsArray = Object.keys(skillsData);
    const angle = (index * 360) / skillsArray.length;
    const radius = getSizeValue(size, 80, 120, 100);

    const x = Math.cos(((angle - 90) * Math.PI) / 180) * radius;
    const y = Math.sin(((angle - 90) * Math.PI) / 180) * radius;

    return { x, y, angle };
  };

  const getSkillPoint = (skillKey: string, index: number) => {
    // Get angle from position
    getSkillPosition(skillKey, index); // For consistency check
    const skill = skillsData[skillKey];
    const valueRadius = (skill.value / 100) * getSizeValue(size, 60, 90, 75);

    const pointX =
      Math.cos(
        (((index * 360) / Object.keys(skillsData).length - 90) * Math.PI) / 180,
      ) * valueRadius;
    const pointY =
      Math.sin(
        (((index * 360) / Object.keys(skillsData).length - 90) * Math.PI) / 180,
      ) * valueRadius;

    return { x: pointX, y: pointY };
  };

  const generatePolygonPoints = (): string => {
    const skillsArray = Object.keys(skillsData);
    return skillsArray
      .map((skillKey, index) => {
        const point = getSkillPoint(skillKey, index);
        return `${point.x},${point.y}`;
      })
      .join(" ");
  };

  const generateGridLines = () => {
    const lines: React.ReactNode[] = [];
    const maxRadius = getSizeValue(size, 80, 120, 100);

    for (let i = 1; i <= 5; i++) {
      const radius = (maxRadius * i) / 5;
      const points = Object.keys(skillsData)
        .map((skillKey, index) => {
          const angle = (index * 360) / Object.keys(skillsData).length;
          const x = Math.cos(((angle - 90) * Math.PI) / 180) * radius;
          const y = Math.sin(((angle - 90) * Math.PI) / 180) * radius;
          return `${x},${y}`;
        })
        .join(" ");

      lines.push(
        <polygon
          key={i}
          points={points}
          fill="none"
          stroke="#e0e0e0"
          strokeWidth="1"
          opacity="0.5"
        />,
      );
    }

    return lines;
  };

  const generateAxisLines = () => {
    const skillsArray = Object.keys(skillsData);
    const maxRadius = getSizeValue(size, 80, 120, 100);

    return skillsArray.map((skillKey, index) => {
      const angle = (index * 360) / skillsArray.length;
      const x = Math.cos(((angle - 90) * Math.PI) / 180) * maxRadius;
      const y = Math.sin(((angle - 90) * Math.PI) / 180) * maxRadius;

      return (
        <line
          key={`axis-${skillKey}`}
          x1="0"
          y1="0"
          x2={x}
          y2={y}
          stroke="#e0e0e0"
          strokeWidth="1"
          opacity="0.5"
        />
      );
    });
  };

  const containerSize = getSizeValue(size, 200, 300, 250);

  return (
    <div className={`hexagon-skills-container ${size}`}>
      <div className="hexagon-skills-header">
        <h3>{displayTitle}</h3>
        {editable && (
          <button
            className="edit-skills-btn"
            onClick={() => setIsEditing(!isEditing)}
          >
            {isEditing ? t("common:skills.done") : t("common:actions.edit")}
          </button>
        )}
      </div>

      <div className="hexagon-skills-content">
        <div className="hexagon-chart">
          <svg
            width={containerSize}
            height={containerSize}
            viewBox={`-${containerSize / 2} -${containerSize / 2} ${containerSize} ${containerSize}`}
            className="hexagon-svg"
          >
            {/* Grid lines */}
            {generateGridLines()}

            {/* Axis lines */}
            {generateAxisLines()}

            {/* Skill area polygon */}
            <polygon
              points={generatePolygonPoints()}
              fill="rgba(25, 118, 210, 0.2)"
              stroke="#1976D2"
              strokeWidth="2"
              className="skill-area"
            />

            {/* Skill points */}
            {Object.keys(skillsData).map((skillKey, index) => {
              const point = getSkillPoint(skillKey, index);
              const skill = skillsData[skillKey];
              const IconComponent = skill.icon || getIconForSkill(skillKey); // Fallback icon

              return (
                <g key={skillKey}>
                  <circle
                    cx={point.x}
                    cy={point.y}
                    r="6"
                    fill={skill.color || "#1976D2"}
                    stroke="#fff"
                    strokeWidth="2"
                    className="skill-point"
                    onMouseEnter={() => setHoveredSkill(skillKey)}
                    onMouseLeave={() => setHoveredSkill(null)}
                  />

                  {/* Skill icon */}
                  <foreignObject
                    x={point.x - 8}
                    y={point.y - 8}
                    width="16"
                    height="16"
                    className="skill-icon"
                  >
                    {IconComponent && <IconComponent size={12} color="#fff" />}
                  </foreignObject>
                </g>
              );
            })}

            {/* Skill labels */}
            {Object.keys(skillsData).map((skillKey, index) => {
              const skill = skillsData[skillKey];
              const labelRadius = getSizeValue(size, 100, 140, 120);
              const labelX =
                Math.cos(
                  (((index * 360) / Object.keys(skillsData).length - 90) *
                    Math.PI) /
                    180,
                ) * labelRadius;
              const labelY =
                Math.sin(
                  (((index * 360) / Object.keys(skillsData).length - 90) *
                    Math.PI) /
                    180,
                ) * labelRadius;
              const fontSize = getSizeValue(size, "10", "14", "12");

              return (
                <text
                  key={`label-${skillKey}`}
                  x={labelX}
                  y={labelY}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  className="skill-label"
                  fill={skill.color || "#1976D2"}
                  fontSize={fontSize}
                >
                  {skill.label || skillKey}
                </text>
              );
            })}
          </svg>
        </div>

        {/* Skill details */}
        <div className="skill-details">
          {Object.keys(skillsData).map((skillKey) => {
            const skill = skillsData[skillKey];
            const IconComponent = skill.icon || Code; // Fallback icon
            const isHovered = hoveredSkill === skillKey;

            return (
              <div
                key={skillKey}
                className={`skill-detail ${isHovered ? "hovered" : ""}`}
                onMouseEnter={() => setHoveredSkill(skillKey)}
                onMouseLeave={() => setHoveredSkill(null)}
                onFocus={() => setHoveredSkill(skillKey)}
                onBlur={() => setHoveredSkill(null)}
                tabIndex={0}
                role="listitem"
              >
                <div className="skill-detail-header">
                  {IconComponent && (
                    <IconComponent
                      size={16}
                      color={skill.color || "#1976D2"}
                      className="skill-detail-icon"
                    />
                  )}
                  <span className="skill-detail-label">
                    {skill.label || skillKey}
                  </span>
                  <span className="skill-detail-value">
                    {skill.value || 0}%
                  </span>
                </div>

                {isEditing && (
                  <div className="skill-detail-controls">
                    <input
                      type="range"
                      min="0"
                      max="100"
                      value={skill.value}
                      onChange={(e) =>
                        handleSkillChangeLocal(
                          skillKey,
                          Number.parseInt(e.target.value, 10),
                        )
                      }
                      onMouseUp={(e) => handleSliderMouseUp(e, skillKey)}
                      onTouchEnd={(e) => handleSliderMouseUp(e, skillKey)}
                      className="skill-slider"
                      style={
                        { "--skill-color": skill.color } as React.CSSProperties
                      }
                    />
                    <input
                      type="number"
                      min="0"
                      max="100"
                      value={skill.value}
                      onChange={(e) =>
                        handleNumberInputChange(
                          skillKey,
                          Number.parseInt(e.target.value, 10) || 0,
                        )
                      }
                      onBlur={(e) => handleNumberInputBlur(e, skillKey)}
                      onKeyDown={handleNumberInputKeyDown}
                      className="skill-input"
                    />
                  </div>
                )}

                <div className="skill-progress-bar">
                  <div
                    className="skill-progress-fill"
                    style={{
                      width: `${skill.value}%`,
                      backgroundColor: skill.color,
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Tooltip */}
      {hoveredSkill && (
        <div className="skill-tooltip">
          <div className="tooltip-content">
            <strong>{skillsData[hoveredSkill].label}</strong>
            <div className="tooltip-value">
              {skillsData[hoveredSkill].value}%
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default HexagonSkills;
